import { ref } from 'vue';
import { useMutation } from '@tanstack/vue-query';

import { anykrowdApi } from '@/api/anykrowdApi';
import type { FreeTickets, Voucher, VoucherErrorMessages, VoucherMetaData, VoucherRedeemResult } from '@/shared/interfaces/voucher';

const voucherErrorMap: Record<string, string> = {
	invalid_voucher_code: 'top_up.redeem_voucher.errors.invalid_voucher',
	voucher_pending: 'top_up.redeem_voucher.errors.voucher_pending',
	voucher_used: 'top_up.redeem_voucher.errors.voucher_used',
	voucher_expired: 'top_up.redeem_voucher.errors.voucher_expired',
	voucher_claim_limit_reached: 'top_up.redeem_voucher.errors.voucher_claim_limit_reached',
	voucher_user_limit_reached: 'top_up.redeem_voucher.errors.voucher_user_limit_reached',
	voucher_configuration_issue: 'top_up.redeem_voucher.errors.voucher_configuration_issue',
};

const normalizeRedeemedItems = (redeemedItems: Voucher['redeemed_items']): FreeTickets[] => {
	return Object.values(redeemedItems).flatMap((item) => (Array.isArray(item) ? item : [item]));
};

// Redeem voucher
const _redeemVoucher = async (voucher: string): Promise<VoucherRedeemResult> => {
	let voucherMetaData: VoucherMetaData | undefined;
	let voucherRedeemedItems: FreeTickets[] = [];
	let error = <string | null>null;
	let errorCode = <string | null>null;
	let errorMessages = <VoucherErrorMessages | undefined>undefined;
	let msg = <string | null>null;

	// Send POST
	return await anykrowdApi
		.post('/voucher/redeem', { voucher_code: voucher })
		.then(({ data }) => {
			const voucherResult: Voucher = data;

			const responseErrorCode = data.error as string | undefined;

			if (responseErrorCode) {
				errorCode = responseErrorCode;
				errorMessages = data.error_messages;
				error = voucherErrorMap[responseErrorCode] ?? 'top_up.redeem_voucher.errors.voucher_configuration_issue';
			}

			if (voucherResult.voucher_meta_data) {
				voucherMetaData = voucherResult.voucher_meta_data;
			}

			if (voucherMetaData && voucherMetaData['status'] === 'pending') {
				error = 'top_up.redeem_voucher.voucher_redeemed_pending';
			} else if (voucherResult.redeemed_items) {
				voucherRedeemedItems = normalizeRedeemedItems(voucherResult.redeemed_items);
				msg = 'top_up.redeem_voucher.voucher_redeemed';
			}

			return {
				error,
				errorCode,
				errorMessages,
				msg,
				voucherMetaData,
				voucherRedeemedItems,
			};
		})
		.catch((res) => {
			errorCode = res.response?.data?.error ?? null;
			errorMessages = res.response?.data?.error_messages;
			error = voucherErrorMap[errorCode ?? ''] ?? 'top_up.redeem_voucher.errors.voucher_configuration_issue';

			return {
				error,
				errorCode,
				errorMessages,
				msg,
				voucherMetaData,
				voucherRedeemedItems,
			};
		});
};

const useVoucher = () => {
	const voucherText = ref('');

	const { data: voucherData, error: errorVoucher, isLoading: isLoadingVoucher, mutate: redeemVoucher } = useMutation(_redeemVoucher);

	return {
		errorVoucher,
		isLoadingVoucher,
		redeemVoucher,
		voucherData,
		voucherText,
	};
};

export default useVoucher;
